import React, { useState, useEffect, useRef } from "react";
import api from "../../utils/Api";
import { ArrowUp, ArrowDown, MapPin, Edit2, FileText, FileSpreadsheet } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function Fichajes() {
  const today = new Date().toISOString().split("T")[0];

  const [fichajes, setFichajes] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    fechaInicio: today,
    fechaFin: today,
    empleado: "",
  });
  const [sortField, setSortField] = useState("fecha_hora");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [selectedFichaje, setSelectedFichaje] = useState(null);
  const [editHoras, setEditHoras] = useState({
    fecha: "",
    hora_entrada: "",
    hora_salida: "",
    hora_entrada_descanso: "",
    hora_fin_descanso: "",
    comentarios: "",
  });
  const [motivo, setMotivo] = useState("");
  const [toast, setToast] = useState({ message: "", type: "" });

  const perPage = 10;
  const debounceRef = useRef(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };

  const fetchFichajes = async (overrideFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        fecha_inicio: overrideFilters.fechaInicio || undefined,
        fecha_fin: overrideFilters.fechaFin || undefined,
        empleado: overrideFilters.empleado || undefined,
        sort_field: sortField,
        sort_order: sortOrder,
        page: currentPage,
        per_page: perPage,
      };
      const { data } = await api.get("/fichajes", { params });
      setFichajes({
        data: data.data || [],
        total: data.total || 0,
      });
    } catch (error) {
      console.error(error);
      showToast("Error al cargar los fichajes", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFichajes();
  }, [sortField, sortOrder, currentPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));

    if (name === "empleado") {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setCurrentPage(1);
        fetchFichajes({ ...filters, [name]: value });
      }, 300);
    }
  };

  const applyFilters = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchFichajes();
  };

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const totalPages = Math.ceil(fichajes.total / perPage) || 1;

  const openMap = (lat, lng) => {
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  // ----------------- PDF -----------------
  const exportPDF = async (dataToExport) => {
    let empresa = {};
    let imgData = null;

    try {
      const res = await api.get("/empresa");
      empresa = res.data;
      const logoRes = await api.get("/empresa/logo");
      imgData = logoRes.data?.logo || null;
    } catch (err) {
      console.error("Error al obtener empresa o logo:", err);
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const NAVY   = [15, 23, 42];
    const ORANGE = [255, 122, 0];
    const WHITE  = [255, 255, 255];
    const LIGHT  = [248, 250, 252];
    const MUTED  = [100, 116, 139];

    const generatedAt = new Date().toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

    // ── Cabecera azul oscuro ──────────────────────────────────────────────
    doc.setFillColor(...NAVY);
    doc.rect(0, 0, pageW, 72, "F");

    // Logo empresa
    if (imgData) {
      doc.addImage(imgData, "PNG", 28, 10, 52, 52);
    } else {
      // Placeholder "T" naranja si no hay logo
      doc.setFillColor(...ORANGE);
      doc.roundedRect(28, 16, 40, 40, 6, 6, "F");
      doc.setFontSize(20);
      doc.setTextColor(...WHITE);
      doc.setFont(undefined, "bold");
      doc.text("T", 48, 42, { align: "center" });
    }

    // Nombre empresa
    const nombreEmpresa = empresa.nombre_fiscal || "Empresa";
    doc.setFontSize(16);
    doc.setTextColor(...WHITE);
    doc.setFont(undefined, "bold");
    doc.text(nombreEmpresa, 84, 32);

    // Info empresa — línea 2
    const infoLine = [
      empresa.cif ? `CIF: ${empresa.cif}` : null,
      empresa.direccion || null,
      empresa.telefono ? `Tel: ${empresa.telefono}` : null,
      empresa.email || null,
    ].filter(Boolean).join("   ·   ");

    doc.setFontSize(8.5);
    doc.setFont(undefined, "normal");
    doc.setTextColor(180, 195, 215);
    doc.text(infoLine, 84, 48, { maxWidth: pageW - 200 });

    // Fecha de generación — derecha
    doc.setFontSize(8);
    doc.setTextColor(180, 195, 215);
    doc.text(`Generado: ${generatedAt}`, pageW - 28, 32, { align: "right" });
    doc.text(`Total registros: ${dataToExport.length}`, pageW - 28, 46, { align: "right" });

    // Franja naranja de separación
    doc.setFillColor(...ORANGE);
    doc.rect(0, 72, pageW, 3, "F");

    // ── Subtítulo del informe ─────────────────────────────────────────────
    doc.setFontSize(17);
    doc.setTextColor(...NAVY);
    doc.setFont(undefined, "bold");
    doc.text("Registro de Fichajes", 28, 100);

    // Periodo filtrado
    const periodoLabel = [
      filters.fechaInicio ? `Desde: ${filters.fechaInicio}` : null,
      filters.fechaFin    ? `Hasta: ${filters.fechaFin}`    : null,
      filters.empleado    ? `Empleado: ${filters.empleado}` : null,
    ].filter(Boolean).join("    |    ");

    if (periodoLabel) {
      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.setTextColor(...MUTED);
      doc.text(periodoLabel, 28, 115);
    }

    // ── Tabla ─────────────────────────────────────────────────────────────
    const colWidths = [100, 62, 60, 62, 62, 72, 72, 52, 52, 48];

    autoTable(doc, {
      head: [[
        "Empleado", "DNI", "Fecha",
        "H. Entrada", "H. Salida", "Ini. Descanso", "Fin Descanso",
        "Tipo", "Ubicación", "Incidencia",
      ]],
      body: dataToExport.map((f) => [
        `${f.user?.nombre ?? ""} ${f.user?.apellidos ?? ""}`.trim(),
        f.user?.dni ?? "—",
        f.fecha ?? "—",
        f.hora_entrada              ? f.hora_entrada.slice(0, 5)              : "—",
        f.hora_salida               ? f.hora_salida.slice(0, 5)               : "—",
        f.hora_entrada_descanso     ? f.hora_entrada_descanso.slice(0, 5)     : "—",
        f.hora_fin_descanso         ? f.hora_fin_descanso.slice(0, 5)         : "—",
        (f.tipo ?? "").charAt(0).toUpperCase() + (f.tipo ?? "").slice(1),
        f.ubicacion ? "Sí" : "—",
        f.incidencia ? "⚠ Sí" : "No",
      ]),
      startY: periodoLabel ? 126 : 112,
      columnStyles: colWidths.reduce((acc, w, i) => { acc[i] = { cellWidth: w }; return acc; }, {}),
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 5, right: 6, bottom: 5, left: 6 },
        textColor: NAVY,
        font: "helvetica",
        overflow: "ellipsize",
      },
      headStyles: {
        fillColor: NAVY,
        textColor: WHITE,
        fontStyle: "bold",
        fontSize: 8,
        cellPadding: { top: 6, right: 6, bottom: 6, left: 6 },
        halign: "left",
      },
      alternateRowStyles: { fillColor: LIGHT },
      rowPageBreak: "auto",
      showHead: "everyPage",
      theme: "plain",
      // Pie de página con número de página
      didDrawPage: (data) => {
        const pg   = doc.internal.getCurrentPageInfo().pageNumber;
        const total = doc.internal.getNumberOfPages();
        // línea separadora
        doc.setDrawColor(220, 228, 238);
        doc.setLineWidth(0.5);
        doc.line(28, pageH - 22, pageW - 28, pageH - 22);
        // texto pie
        doc.setFontSize(7.5);
        doc.setTextColor(...MUTED);
        doc.setFont(undefined, "normal");
        doc.text("Turnora · Gestión de turnos y fichajes", 28, pageH - 10);
        doc.text(`Página ${pg} de ${total}`, pageW - 28, pageH - 10, { align: "right" });
      },
    });

    // ── Guardar ───────────────────────────────────────────────────────────
    const dateTag = new Date().toISOString().slice(0, 10);
    doc.save(`fichajes_${dateTag}.pdf`);
  };

  // ----------------- CSV -----------------
  const exportCSV = () => {
    const dateTag = new Date().toISOString().slice(0, 10);
    const generatedAt = new Date().toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });

    const sep = ";"; // compatible con Excel España
    const lines = [];

    // Metadatos de cabecera
    lines.push(`# Informe de Fichajes${sep}Turnora`);
    lines.push(`# Generado${sep}${generatedAt}`);
    if (filters.fechaInicio) lines.push(`# Desde${sep}${filters.fechaInicio}`);
    if (filters.fechaFin)    lines.push(`# Hasta${sep}${filters.fechaFin}`);
    if (filters.empleado)    lines.push(`# Empleado${sep}${filters.empleado}`);
    lines.push(`# Total registros${sep}${fichajes.data.length}`);
    lines.push(""); // línea en blanco separadora

    // Cabeceras de columnas
    lines.push([
      "Empleado", "Apellidos", "DNI",
      "Fecha", "Hora Entrada", "Hora Salida",
      "Inicio Descanso", "Fin Descanso",
      "Tipo", "Con Ubicación", "Incidencia", "Comentarios",
    ].join(sep));

    // Datos
    fichajes.data.forEach((f) => {
      const row = [
        f.user?.nombre ?? "",
        f.user?.apellidos ?? "",
        f.user?.dni ?? "",
        f.fecha ?? "",
        f.hora_entrada              ? f.hora_entrada.slice(0, 5)              : "",
        f.hora_salida               ? f.hora_salida.slice(0, 5)               : "",
        f.hora_entrada_descanso     ? f.hora_entrada_descanso.slice(0, 5)     : "",
        f.hora_fin_descanso         ? f.hora_fin_descanso.slice(0, 5)         : "",
        (f.tipo ?? "").charAt(0).toUpperCase() + (f.tipo ?? "").slice(1),
        f.ubicacion ? "Sí" : "No",
        f.incidencia ? "Sí" : "No",
        (f.comentarios ?? "").replace(/"/g, '""'), // escapar comillas
      ].map((v) => `"${v}"`);
      lines.push(row.join(sep));
    });

    // BOM UTF-8 para Excel + descarga
    const bom = "﻿";
    const blob = new Blob([bom + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `fichajes_${dateTag}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ----------------- Edición de fichaje -----------------
  const openEditOffcanvas = (fichaje) => {
    setSelectedFichaje(fichaje);
    setEditHoras({
      fecha: fichaje.fecha || "",
      hora_entrada: fichaje.hora_entrada || "",
      hora_salida: fichaje.hora_salida || "",
      hora_entrada_descanso: fichaje.hora_entrada_descanso || "",
      hora_fin_descanso: fichaje.hora_fin_descanso || "",
      comentarios: fichaje.comentarios || "",
    });
    setMotivo("");
    setOffcanvasOpen(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditHoras((prev) => ({ ...prev, [name]: value }));
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!motivo || motivo.length < 5) {
      showToast("Debes ingresar un motivo válido (mínimo 5 caracteres)", "danger");
      return;
    }
    try {
      const payload = { ...editHoras, motivo };
      await api.put(`/fichajes/${selectedFichaje.id}/editar`, payload);
      setOffcanvasOpen(false);
      fetchFichajes();
      showToast("Fichaje actualizado correctamente", "success");
    } catch (err) {
      console.error(err);
      showToast("Error al actualizar fichaje", "danger");
    }
  };

  const getCamposPorTipo = (tipo) => {
    switch (tipo) {
      case "entrada":
        return ["hora_entrada", "comentarios"];
      case "salida":
        return ["hora_salida", "comentarios"];
      case "descanso":
        return ["hora_entrada_descanso", "hora_fin_descanso", "comentarios"];
      default:
        return ["comentarios"];
    }
  };

  // ----------------- Render -----------------
  return (
    <div>
      <h1 className="tn-page-title">Registro de Fichajes</h1>
      <p className="tn-page-sub">Consulta y edita los fichajes de todos los empleados</p>

      {/* Toast */}
      {toast.message && (
        <div className={`tn-toast tn-toast--${toast.type}`} role="alert">
          {toast.message}
          <button style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "inherit" }} onClick={() => setToast({ message: "", type: "" })}>✕</button>
        </div>
      )}

      {/* Filtros */}
      <form className="tn-filters" onSubmit={applyFilters}>
        <div className="tn-filter-group">
          <label className="tn-label">Fecha inicio</label>
          <input type="date" className="tn-input" name="fechaInicio" value={filters.fechaInicio} onChange={handleFilterChange} />
        </div>
        <div className="tn-filter-group">
          <label className="tn-label">Fecha fin</label>
          <input type="date" className="tn-input" name="fechaFin" value={filters.fechaFin} onChange={handleFilterChange} />
        </div>
        <div className="tn-filter-group">
          <label className="tn-label">Empleado / DNI</label>
          <input type="text" className="tn-input" name="empleado" placeholder="Nombre o DNI…" value={filters.empleado} onChange={handleFilterChange} />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
          <button type="submit" className="tn-btn tn-btn--primary">Filtrar</button>
          <button type="button" className="tn-btn tn-btn--ghost" style={{ display: "flex", alignItems: "center", gap: 5 }} onClick={exportCSV}>
            <FileSpreadsheet size={14} /> CSV
          </button>
          <button type="button" className="tn-btn tn-btn--ghost" style={{ display: "flex", alignItems: "center", gap: 5 }} onClick={() => exportPDF(fichajes.data)}>
            <FileText size={14} /> PDF
          </button>
        </div>
      </form>

      {/* Tabla */}
      <div className="tn-table-wrap">
        {loading ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--clr-muted)", fontSize: 14 }}>Cargando fichajes…</div>
        ) : fichajes.data.length === 0 ? (
          <div style={{ padding: "40px 20px", textAlign: "center", color: "var(--clr-muted)", fontSize: 14 }}>No hay fichajes para los filtros aplicados</div>
        ) : (
          <>
            <table className="tn-table">
              <thead>
                <tr>
                  <th>Empleado</th>
                  <th>DNI</th>
                  <th onClick={() => toggleSort("fecha_hora")} style={{ cursor: "pointer", userSelect: "none" }}>
                    Fecha {sortField === "fecha_hora" && (sortOrder === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                  </th>
                  <th>Entrada</th>
                  <th>Salida</th>
                  <th>Ini. desc.</th>
                  <th>Fin desc.</th>
                  <th>Tipo</th>
                  <th>Ubicación</th>
                  <th>Incid.</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {fichajes.data.map(f => (
                  <tr key={f.id}>
                    <td data-label="Empleado" style={{ fontWeight: 600 }}>{f.user?.nombre} {f.user?.apellidos}</td>
                    <td data-label="DNI" style={{ color: "var(--clr-muted)" }}>{f.user?.dni}</td>
                    <td data-label="Fecha">{f.fecha}</td>
                    <td data-label="Entrada">{f.hora_entrada || <span style={{ color: "var(--clr-muted-lt)" }}>—</span>}</td>
                    <td data-label="Salida">{f.hora_salida || <span style={{ color: "var(--clr-muted-lt)" }}>—</span>}</td>
                    <td data-label="Ini. desc.">{f.hora_entrada_descanso || <span style={{ color: "var(--clr-muted-lt)" }}>—</span>}</td>
                    <td data-label="Fin desc.">{f.hora_fin_descanso || <span style={{ color: "var(--clr-muted-lt)" }}>—</span>}</td>
                    <td data-label="Tipo"><span className="tn-badge tn-badge--slate" style={{ textTransform: "capitalize" }}>{f.tipo}</span></td>
                    <td data-label="Ubicación">
                      {f.ubicacion
                        ? <button className="tn-btn tn-btn--ghost" style={{ padding: "4px 10px", fontSize: 12, minHeight: 30 }} onClick={() => openMap(f.latitud, f.longitud)}><MapPin size={13} /> Ver</button>
                        : <span style={{ color: "var(--clr-muted-lt)" }}>—</span>}
                    </td>
                    <td data-label="Incidencia">
                      {f.incidencia
                        ? <span className="tn-badge tn-badge--yellow">Sí</span>
                        : <span style={{ color: "var(--clr-muted-lt)", fontSize: 12 }}>No</span>}
                    </td>
                    <td data-label="Editar">
                      <button
                        className="tn-btn tn-btn--primary"
                        style={{ padding: "5px 10px", minHeight: 30, fontSize: 12 }}
                        onClick={() => openEditOffcanvas(f)}
                        disabled={!f.incidencia}
                        title={!f.incidencia ? "Requiere una incidencia vinculada" : undefined}
                      >
                        <Edit2 size={13} /> Editar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tn-pagination">
              <button className="tn-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}>‹</button>
              {[...Array(totalPages)].map((_, idx) => (
                <button key={idx} className={`tn-page-btn${currentPage === idx + 1 ? " active" : ""}`} onClick={() => setCurrentPage(idx + 1)}>{idx + 1}</button>
              ))}
              <button className="tn-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}>›</button>
            </div>
          </>
        )}
      </div>

      {/* Offcanvas de edición */}
      {offcanvasOpen && selectedFichaje && (
        <>
          <div className="tn-overlay" onClick={() => setOffcanvasOpen(false)} />
          <div className="tn-offcanvas">
            <div className="tn-offcanvas__header">
              <span className="tn-offcanvas__title">Editar fichaje — <span style={{ color: "var(--clr-accent)", textTransform: "capitalize" }}>{selectedFichaje.tipo}</span></span>
              <button className="tn-close" onClick={() => setOffcanvasOpen(false)}>✕</button>
            </div>
            <div className="tn-offcanvas__body">
              <form onSubmit={submitEdit}>
                <div style={{ marginBottom: 14 }}>
                  <label className="tn-label">Fecha</label>
                  <input type="date" className="tn-input" name="fecha" value={editHoras.fecha} onChange={handleEditChange} />
                </div>
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_entrada") && (
                  <div style={{ marginBottom: 14 }}>
                    <label className="tn-label">Hora entrada</label>
                    <input type="time" className="tn-input" step="1" name="hora_entrada" value={editHoras.hora_entrada} onChange={handleEditChange} />
                  </div>
                )}
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_salida") && (
                  <div style={{ marginBottom: 14 }}>
                    <label className="tn-label">Hora salida</label>
                    <input type="time" className="tn-input" step="1" name="hora_salida" value={editHoras.hora_salida} onChange={handleEditChange} />
                  </div>
                )}
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_entrada_descanso") && (
                  <div style={{ marginBottom: 14 }}>
                    <label className="tn-label">Inicio descanso</label>
                    <input type="time" className="tn-input" step="1" name="hora_entrada_descanso" value={editHoras.hora_entrada_descanso} onChange={handleEditChange} />
                  </div>
                )}
                {getCamposPorTipo(selectedFichaje.tipo).includes("hora_fin_descanso") && (
                  <div style={{ marginBottom: 14 }}>
                    <label className="tn-label">Fin descanso</label>
                    <input type="time" className="tn-input" step="1" name="hora_fin_descanso" value={editHoras.hora_fin_descanso} onChange={handleEditChange} />
                  </div>
                )}
                <div style={{ marginBottom: 14 }}>
                  <label className="tn-label">Comentarios</label>
                  <textarea className="tn-textarea" name="comentarios" value={editHoras.comentarios} onChange={handleEditChange} rows={3} />
                </div>
                <hr className="tn-divider" />
                <div style={{ marginBottom: 14 }}>
                  <label className="tn-label">Motivo de la edición <span style={{ color: "var(--clr-danger)" }}>*</span></label>
                  <textarea className="tn-textarea" value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} placeholder="Describe el motivo del cambio (mín. 5 caracteres)…" />
                </div>
                <div className="tn-offcanvas__footer" style={{ padding: 0 }}>
                  <button type="button" className="tn-btn tn-btn--ghost" onClick={() => setOffcanvasOpen(false)}>Cancelar</button>
                  <button type="submit" className="tn-btn tn-btn--success">Guardar cambios</button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
